import type { MemberId, Ticket } from '../../domain/types';
import { appendShare } from './create-view';
import { statusLabels } from './home-view';
import { createDiagnosticSummary } from './diagnostics';

export type ReceiveViewModel =
  | { phase: 'validating' }
  | { phase: 'preview'; title: string; detail: string; actionLabel: string }
  | { phase: 'saved'; responseUrl: string }
  | { phase: 'ticket-preview'; ticket: Ticket; isNew: boolean; memberId: MemberId; partnerName: string }
  | {
    phase: 'ticket-result';
    outcome: 'applied' | 'duplicate' | 'stale';
    ticket: Ticket;
    responseUrl: string;
    shareCard?: Blob;
    cardError?: string;
    message: string;
    partnerName: string;
  }
  | { phase: 'conflict'; local: Ticket; incoming: Ticket }
  | { phase: 'error'; message: string; errorCode: string; appVersion: string };

export interface ReceiveViewActions {
  onConfirm?: () => void | Promise<void>;
  onAccept?: () => void | Promise<void>;
  onReject?: (note: string) => void | Promise<void>;
  onResolveConflict?: (keep: 'local' | 'incoming') => void | Promise<void>;
  onCopy?: (value: string, status: HTMLElement) => void | Promise<void>;
  onShare?: (url: string, card: Blob | null, status: HTMLElement) => void | Promise<void>;
}

export function renderReceiveView(
  container: HTMLElement,
  model: ReceiveViewModel,
  actions: ReceiveViewActions = {},
): void {
  container.replaceChildren();
  const section = document.createElement('section');
  section.className = 'page-card stack';

  if (model.phase === 'validating') {
    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.textContent = '正在验证链接…';
    section.append(status);
  }

  if (model.phase === 'preview') {
    const title = document.createElement('h1');
    title.textContent = model.title;
    const detail = document.createElement('p');
    detail.textContent = model.detail;
    const button = document.createElement('button');
    button.className = 'button button--primary';
    button.type = 'button';
    button.textContent = model.actionLabel;
    button.addEventListener('click', () => void actions.onConfirm?.());
    section.append(title, detail, button);
  }

  if (model.phase === 'saved') {
    const title = document.createElement('h1');
    title.setAttribute('role', 'status');
    title.textContent = '已收下这条更新';
    const next = document.createElement('p');
    next.className = 'next-step';
    next.textContent = '下一步：复制回应链接发给 TA';
    const label = document.createElement('label');
    label.className = 'field';
    label.append(document.createTextNode('回应链接'));
    const input = document.createElement('input');
    input.readOnly = true;
    input.value = model.responseUrl;
    input.dataset.testid = 'share-link';
    label.append(input);
    const copyStatus = document.createElement('p');
    copyStatus.className = 'muted';
    copyStatus.setAttribute('role', 'status');
    const button = document.createElement('button');
    button.className = 'button button--primary';
    button.type = 'button';
    button.textContent = '复制回应链接发给 TA';
    button.addEventListener('click', () => void actions.onCopy?.(model.responseUrl, copyStatus));
    section.append(title, next, label, button, copyStatus);
  }

  if (model.phase === 'ticket-preview') {
    const type = document.createElement('p');
    type.className = 'eyebrow';
    type.textContent = model.ticket.type === 'reward' ? '🎁 收到奖励单' : '📝 收到小罚单';
    const title = document.createElement('h1');
    title.textContent = model.ticket.title;
    const head = model.ticket.events.find((event) => event.id === model.ticket.headEventId);
    const source = document.createElement('p');
    source.className = 'muted';
    source.textContent = `来自：${head?.actor === model.memberId ? '本机身份' : model.partnerName}`;
    const description = document.createElement('p');
    description.textContent = model.ticket.description || '没有补充说明';
    const outcome = document.createElement('p');
    outcome.className = 'receive-outcome';
    outcome.textContent = `${model.ticket.type === 'reward' ? '奖励内容' : '惩罚内容'}：${model.ticket.outcome}`;
    const status = document.createElement('span');
    status.className = `status-badge status-badge--${model.ticket.state}`;
    status.textContent = statusLabels[model.ticket.state];
    section.append(type, title, source, description, outcome, status);

    const canRespond = model.isNew
      && model.ticket.state === 'pending_response'
      && model.memberId === model.ticket.recipientId;
    if (canRespond) {
      const accept = document.createElement('button');
      accept.className = 'button button--primary';
      accept.type = 'button';
      accept.textContent = '收下并接受';
      accept.addEventListener('click', () => void actions.onAccept?.());
      const reject = document.createElement('form');
      reject.className = 'stack reject-form';
      reject.innerHTML = '<label class="field">拒绝原因<textarea name="note" maxlength="120" rows="3" required></textarea></label><button class="button button--danger" type="submit">拒绝并说明原因</button>';
      reject.addEventListener('submit', (event) => {
        event.preventDefault();
        const data = new FormData(reject);
        const note = data.get('note');
        void actions.onReject?.(typeof note === 'string' ? note : '');
      });
      section.append(accept, reject);
    } else {
      const confirm = document.createElement('button');
      confirm.className = 'button button--primary';
      confirm.type = 'button';
      confirm.textContent = '收下这条更新';
      confirm.addEventListener('click', () => void actions.onConfirm?.());
      section.append(confirm);
    }
  }

  if (model.phase === 'ticket-result') {
    const title = document.createElement('h1');
    title.setAttribute('role', 'status');
    title.textContent = model.message;
    const ticketTitle = document.createElement('p');
    ticketTitle.innerHTML = `<b>${escapeText(model.ticket.title)}</b>`;
    const status = document.createElement('span');
    status.className = `status-badge status-badge--${model.ticket.state}`;
    status.textContent = statusLabels[model.ticket.state];
    section.append(title, ticketTitle, status);
    const latestNote = [...model.ticket.events].reverse().find((event) => event.note)?.note;
    if (latestNote) {
      const note = document.createElement('p');
      note.className = 'event-note';
      note.textContent = latestNote;
      section.append(note);
    }
    const next = document.createElement('p');
    next.className = 'next-step';
    next.textContent = `下一步：复制回应链接发给 ${model.partnerName}`;
    section.append(next);
    appendShare(section, model.responseUrl, model.shareCard ?? null, model.cardError, actions);
    const detail = document.createElement('a');
    detail.className = 'button button--link';
    detail.href = `#ticket/${encodeURIComponent(model.ticket.id)}`;
    detail.textContent = '查看单据详情';
    section.append(detail);
  }

  if (model.phase === 'conflict') {
    const title = document.createElement('h1');
    title.textContent = '发现两条不同更新';
    const message = document.createElement('p');
    message.setAttribute('role', 'alert');
    message.id = 'ticket-conflict-alert';
    message.textContent = '这条更新与本机记录冲突；两条记录都已保留，请稍后选择要保留的分支。';
    section.append(title, message);
    const form = document.createElement('form');
    form.className = 'conflict-choices stack';
    form.setAttribute('aria-describedby', message.id);
    form.append(
      conflictChoice('保留本机分支', 'local', model.local),
      conflictChoice('保留链接分支', 'incoming', model.incoming),
    );
    const confirm = document.createElement('button');
    confirm.className = 'button button--primary';
    confirm.type = 'submit';
    confirm.disabled = true;
    confirm.textContent = '确认解决冲突';
    form.addEventListener('change', () => {
      confirm.disabled = form.dataset.submitting === 'true'
        || form.querySelector<HTMLInputElement>('input[name="conflict-branch"]:checked') === null;
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const selected = new FormData(form).get('conflict-branch');
      if (selected !== 'local' && selected !== 'incoming') return;
      form.dataset.submitting = 'true';
      confirm.disabled = true;
      const radios = [...form.querySelectorAll<HTMLInputElement>('input[name="conflict-branch"]')];
      for (const radio of radios) radio.disabled = true;
      const unlock = (): void => {
        if (!form.isConnected) return;
        delete form.dataset.submitting;
        for (const radio of radios) radio.disabled = false;
        confirm.disabled = form.querySelector<HTMLInputElement>('input[name="conflict-branch"]:checked') === null;
      };
      void Promise.resolve(actions.onResolveConflict?.(selected)).then(unlock, unlock);
    });
    form.append(confirm);
    section.append(form);
  }

  if (model.phase === 'error') {
    const title = document.createElement('h1');
    title.textContent = '无法收下这条链接';
    const message = document.createElement('p');
    message.setAttribute('role', 'alert');
    message.id = 'protocol-error';
    message.textContent = model.message;
    section.setAttribute('aria-describedby', message.id);
    const summary = createDiagnosticSummary(model.errorCode, model.appVersion);
    const diagnostic = document.createElement('pre');
    diagnostic.className = 'diagnostic';
    diagnostic.dataset.testid = 'diagnostic-summary';
    diagnostic.textContent = summary;
    const copyStatus = document.createElement('p');
    copyStatus.className = 'muted';
    copyStatus.setAttribute('role', 'status');
    const button = document.createElement('button');
    button.className = 'button';
    button.type = 'button';
    button.textContent = '复制诊断摘要';
    button.addEventListener('click', () => void actions.onCopy?.(summary, copyStatus));
    section.append(title, message, diagnostic, button, copyStatus);
  }

  container.append(section);
}

function conflictChoice(label: string, value: 'local' | 'incoming', ticket: Ticket): HTMLElement {
  const latest = ticket.events.find((event) => event.id === ticket.headEventId) ?? ticket.events[ticket.events.length - 1]!;
  const card = document.createElement('label');
  card.className = 'conflict-choice stack';
  const input = document.createElement('input');
  input.type = 'radio';
  input.name = 'conflict-branch';
  input.value = value;
  const heading = document.createElement('h2');
  heading.textContent = label;
  const detail = document.createElement('p');
  detail.textContent = `操作方：${latest.actor === ticket.creatorId ? '开单方' : '接单方'} · 动作：${latest.action} · 时间：${latest.at}`;
  const status = document.createElement('p');
  status.textContent = `结果：${statusLabels[ticket.state]} · 备注：${latest.note || '无'}`;
  card.append(input, heading, detail, status);
  return card;
}

function escapeText(value: string): string {
  const node = document.createElement('span');
  node.textContent = value;
  return node.innerHTML;
}
